import styled from '@emotion/styled';
import { forwardRef, useMemo } from 'react';
import * as tokens from '@tokens';
import { Icon } from '@components/Icon/Icon';
import type { DropdownBaseItemProps } from './DropdownBaseItem';
import { DropdownBaseItem } from './DropdownBaseItem';
import type { IconName } from '@components/Icon/BaseIcon';
import type { BadgeProps } from '@components/Badge/Badge';
import { Badge } from '@components/Badge/Badge';
import type { PlanBadgeProps } from '@components/PlanBadge/PlanBadge';
import { PlanBadge } from '@components/PlanBadge/PlanBadge';

export interface DropdownItemProps
  extends Omit<DropdownBaseItemProps<'button'>, 'children' | 'as'> {
  /**
   * Icon to be shown in the item.
   */
  icon?: IconName;
  /**
   * DropdownItem content.
   */
  children: string;

  /**
   * Should the item be disabled.
   */
  isDisabled?: boolean;

  /**
   * Not available.
   */
  isExternal?: undefined;

  /**
   * Badge to be shown next to the item text.
   */
  badge?: BadgeProps['kind'];

  /**
   * PlanBadge to be shown next to the item text.
   */
  planBadge?: PlanBadgeProps['kind'];
}

const StyledIcon = styled.span`
  flex-shrink: 1;
  margin-right: ${tokens.spacer.x1};
  display: inline-flex;
`;

const StyledItemText = styled.span`
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
`;

const StyledSelectedIcon = styled.span`
  flex-shrink: 1;
  margin-left: ${tokens.spacer.x1};
  display: inline-flex;
`;

/**
 * Use DropdownMenu.Item for actions inside DropdownMenu.
 *
 * {@link https://zeroheight.com/76c439891/p/96bb9e-dropdown-menu/b/72fb4d Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-dropdownmenu Code documentation}
 *
 * @example
 * <DropdownMenu.Item icon={} onClick={}>Action Text</DropdownMenu.Item>
 */
export const DropdownItem = forwardRef<HTMLButtonElement, DropdownItemProps>(
  (
    {
      children,
      icon,
      isDisabled = false,
      isHidden = false,
      isSelected = false,
      kind = 'ghost',
      hasDivider = false,
      badge,
      planBadge,
      ...baseProps
    }: DropdownItemProps,
    ref,
  ): JSX.Element => {
    const isSelectedIconVisible = useMemo(
      () => isSelected && !isDisabled,
      [isSelected, isDisabled],
    );

    return (
      <DropdownBaseItem
        {...baseProps}
        as="button"
        isDisabled={isDisabled}
        isHidden={isHidden}
        isSelected={isSelected}
        title={children}
        ref={ref}
        kind={kind}
        hasDivider={hasDivider}
      >
        {icon && (
          <StyledIcon data-testid="dropdown-item-icon">
            <Icon icon={icon} size="m" />
          </StyledIcon>
        )}
        <StyledItemText>{children}</StyledItemText>
        {badge && <Badge kind={badge} size="s" />}
        {planBadge && <PlanBadge kind={planBadge} size="s" />}
        {isSelectedIconVisible && (
          <StyledSelectedIcon data-testid="dropdown-item-selected-icon">
            <Icon icon="selected" size="m" />
          </StyledSelectedIcon>
        )}
      </DropdownBaseItem>
    );
  },
);

DropdownItem.displayName = 'DropdownItem';
